import React from 'react';

import { Icon, type IconName } from '../Icon';
import { Veil, TopRow, BottomRow, Header, HeaderIcon, Title, Lock, Center, Caption, type LockCorner } from './LockVeil.styles';

export type { LockCorner };

export type LockVeilProps = {
  /** What is unlocking — the channel name. Rendered in the header, where the enabled
   *  card shows its own name. */
  name?: string;
  /** Icon shown before the name in the header (the channel's own icon). */
  icon?: IconName;
  /** Centre caption. Defaults to "Unlocking soon"; pass `null` to omit it entirely (e.g. a
   *  compact/button-sized lock that shows the reason in a hover tooltip instead). */
  text?: string | null;
  /** Header icon size in px — set it to the host card's header icon size so the locked
   *  card matches its enabled neighbours. */
  iconSize?: number;
  /** Header title font size in px — set it to the host card's header font size, same reason. */
  fontSize?: number;
  /** Icon→title spacing in px — match the host card's header gap so the title overlays
   *  its live neighbour exactly. */
  headerGap?: number;
  /** Inset of the header + padlock from the card edges — match the host card's padding
   *  so the header lands over the enabled card's header. */
  pad?: number | string;
  /** Which corner the padlock occupies, to land where the host card's chevron sits.
   *  Defaults to `top-right`. */
  lockCorner?: LockCorner;
  /** Padlock size in px — match the host card's chevron size (typically 24). */
  lockSize?: number;
  /** Corner radius to match the host card; defaults to inheriting the host's radius. */
  radius?: number | string;
  className?: string;
};

const DEFAULT_ICON_SIZE = 22;
const DEFAULT_FONT_SIZE = 16;
const DEFAULT_HEADER_GAP = 12;
const DEFAULT_PAD = 12;
const DEFAULT_LOCK_SIZE = 24;

/** Resolve a length-ish prop (`radius` / `pad`) to a CSS value. */
const toLen = (v: number | string): string => (typeof v === 'number' ? `${v}px` : v);

/**
 * Frosted overlay that marks a gated feature card as locked. It mirrors the enabled card:
 * the channel `[icon] {name}` sits in the header (top-left), a **padlock** replaces the
 * chevron (top-right or bottom-right, via `lockCorner`, to land where the live card's
 * chevron is), and a centred "Unlocking soon" caption fills the middle. An 8px backdrop
 * blur under a themed scrim sells the "frost". Size the header (`iconSize` / `fontSize` /
 * `headerGap`) and `pad` to the host card so the locked card overlays its live neighbour
 * exactly. Render it as the last child of a positioned card to cover it; it shields pointer
 * events so the locked card's own controls can't be used. Pass `text={null}` to drop the
 * centre caption for a compact/button-sized lock (show the reason in a hover tooltip on the
 * host instead). Used by the gated acquisition channels (Home grid, Acquisition Lumio grid,
 * daily-limit rows) and the Website-Settings logo-replace control. See
 * `config/acquisitionChannels` / `config/featureFlags` for which features are gated.
 */
export const LockVeil = ({
  name,
  icon,
  text,
  iconSize = DEFAULT_ICON_SIZE,
  fontSize = DEFAULT_FONT_SIZE,
  headerGap = DEFAULT_HEADER_GAP,
  pad = DEFAULT_PAD,
  lockCorner = 'top-right',
  lockSize = DEFAULT_LOCK_SIZE,
  radius,
  className,
}: LockVeilProps): React.ReactElement => {
  const padlock = (
    <Lock aria-hidden="true">
      <Icon name="lock" size={lockSize} />
    </Lock>
  );

  return (
    <Veil className={className} $radius={radius === undefined ? 'inherit' : toLen(radius)} $pad={toLen(pad)}>
      <TopRow>
        {name !== undefined || icon !== undefined ? (
          <Header $gap={headerGap}>
            {icon !== undefined ? (
              <HeaderIcon aria-hidden="true">
                <Icon name={icon} size={iconSize} />
              </HeaderIcon>
            ) : null}
            {name !== undefined ? <Title $fontSize={fontSize} text={name} tag="span" /> : null}
          </Header>
        ) : (
          <span />
        )}
        {lockCorner === 'top-right' ? padlock : null}
      </TopRow>

      <Center>
        {/* `text === null` omits the caption (a compact/button lock shows the reason in a
            tooltip instead). The literal default is kept inline so the i18n extractor harvests it. */}
        {text === null ? null : text !== undefined ? <Caption text={text} tag="span" /> : <Caption text="Unlocking soon" tag="span" />}
      </Center>

      {lockCorner === 'bottom-right' ? <BottomRow>{padlock}</BottomRow> : null}
    </Veil>
  );
};

export default LockVeil;
